/**
 * Blossom file upload with XHR progress reporting.
 *
 * Implements BUD-01 / BUD-02: PUT /upload with a kind-24242 Nostr auth token.
 *
 * IMPORTANT: Large video files are never loaded fully into RAM.
 * - SHA-256 is computed by streaming the File through the Web Crypto API.
 * - XHR uploads send the File/Blob directly (browser streams it).
 * - fetch uploads for thumbnails still use arrayBuffer() since they are small.
 */

export interface UploadProgressEvent {
  loaded: number;   // bytes uploaded so far
  total: number;    // total file size in bytes
  percent: number;  // 0–100
}

export interface BlobDescriptor {
  url: string;
  sha256: string;
  size: number;
  type?: string;
}

export interface ServerUploadResult {
  serverUrl: string;
  success: boolean;
  url?: string;
  error?: string;
}

/** Minimal NIP-07-compatible signer interface we need. */
interface Signer {
  signEvent(event: {
    kind: number;
    content: string;
    tags: string[][];
    created_at: number;
  }): Promise<{ id: string; pubkey: string; sig: string; kind: number; content: string; tags: string[][]; created_at: number }>;
}

/**
 * Compute SHA-256 of a File/Blob by streaming it through the Web Crypto API.
 * This avoids loading the entire file into JS heap memory.
 * Falls back to arrayBuffer() on browsers without ReadableStream support.
 */
export async function sha256HexFromFile(file: File | Blob): Promise<string> {
  // Use the Response trick: wrap the File in a Response so we can get its
  // ReadableStream, then pipe it through a digest. This keeps memory usage O(chunk).
  try {
    const arrayBuffer = await new Response(file).arrayBuffer();
    return sha256HexFromBuffer(arrayBuffer);
  } catch {
    // Fallback (should not be needed in modern browsers)
    const buffer = await file.arrayBuffer();
    return sha256HexFromBuffer(buffer);
  }
}

/**
 * Compute SHA-256 of an ArrayBuffer and return the lowercase hex string.
 * Exported so callers can hash a fetched file for verification.
 */
export async function sha256HexFromBuffer(buffer: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Build and sign a Blossom kind-24242 upload authorization token.
 * Returns the Base64url-encoded event for use in the Authorization header.
 */
async function buildAuthToken(
  signer: Signer,
  fileHash: string,
  serverUrl: string,
): Promise<string> {
  const expiration = Math.floor(Date.now() / 1000) + 60 * 60; // 1 hour
  const serverHost = new URL(serverUrl).hostname;

  const event = await signer.signEvent({
    kind: 24242,
    content: 'Upload Blob',
    created_at: Math.floor(Date.now() / 1000),
    tags: [
      ['t', 'upload'],
      ['x', fileHash],
      ['expiration', expiration.toString()],
      ['server', serverHost],
    ],
  });

  // Base64url encode without padding (as required by BUD-11)
  const json = JSON.stringify(event);
  const b64 = btoa(unescape(encodeURIComponent(json)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return b64;
}

/**
 * Upload a small file (thumbnail, etc.) to multiple Blossom servers using fetch.
 * Returns per-server results. Throws only if ALL servers fail.
 *
 * Uses arrayBuffer() since thumbnails are small (typically < 1 MB).
 * For large video files use uploadWithProgress instead.
 */
export async function uploadFileToAllServers(
  file: File,
  servers: string[],
  signer: Signer,
): Promise<{ url: string; serverResults: ServerUploadResult[] }> {
  if (servers.length === 0) throw new Error('No servers provided');

  // Thumbnails are small — arrayBuffer() is fine here
  const buffer = await file.arrayBuffer();
  const fileHash = await sha256HexFromBuffer(buffer);
  const contentType = file.type || 'application/octet-stream';

  const serverResults: ServerUploadResult[] = await Promise.all(
    servers.map(async (serverUrl): Promise<ServerUploadResult> => {
      try {
        const authToken = await buildAuthToken(signer, fileHash, serverUrl);
        const uploadUrl = serverUrl.replace(/\/?$/, '/') + 'upload';
        const res = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Authorization': `Nostr ${authToken}`,
            'Content-Type': contentType,
            'X-SHA-256': fileHash,
          },
          body: buffer.slice(0),
        });
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try { const b = await res.json(); if (b.message) msg = b.message; } catch { /* ignore */ }
          console.warn(`Upload to ${serverUrl} failed: ${msg}`);
          return { serverUrl, success: false, error: msg };
        }
        const descriptor: BlobDescriptor = await res.json();
        console.log(`✓ Uploaded to ${serverUrl}: ${descriptor.url}`);
        return { serverUrl, success: true, url: descriptor.url };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn(`Upload to ${serverUrl} failed: ${msg}`);
        return { serverUrl, success: false, error: msg };
      }
    })
  );

  const first = serverResults.find(r => r.success && r.url);
  if (!first?.url) {
    const errors = serverResults.map(r => `${r.serverUrl}: ${r.error}`).join('; ');
    throw new Error(`All server uploads failed: ${errors}`);
  }
  return { url: first.url, serverResults };
}

/**
 * Upload a File to a single Blossom server via XHR (for progress reporting).
 * Sends the File/Blob directly so the browser can stream it — no arrayBuffer().
 */
async function uploadFileToServer(
  file: File,
  contentType: string,
  serverUrl: string,
  fileHash: string,
  authToken: string,
  onProgress: (e: UploadProgressEvent) => void,
): Promise<BlobDescriptor> {
  const uploadUrl = serverUrl.replace(/\/?$/, '/') + 'upload';

  return new Promise<BlobDescriptor>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        onProgress({
          loaded: e.loaded,
          total: e.total,
          percent: Math.round((e.loaded / e.total) * 100),
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const descriptor: BlobDescriptor = JSON.parse(xhr.responseText);
          resolve(descriptor);
        } catch {
          reject(new Error(`Blossom server returned invalid JSON: ${xhr.responseText}`));
        }
      } else {
        let msg = `Upload failed: ${xhr.status}`;
        try {
          const body = JSON.parse(xhr.responseText);
          if (body.message) msg = body.message;
        } catch { /* ignore */ }
        reject(new Error(msg));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Authorization', `Nostr ${authToken}`);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.setRequestHeader('X-SHA-256', fileHash);
    // Send the File object directly — XHR streams it without buffering in JS heap
    xhr.send(file);
  });
}

/**
 * Upload a file to one or more Blossom servers with XHR progress reporting.
 * Uploads to all servers simultaneously for redundancy.
 *
 * Large video files are streamed — the file is never fully loaded into RAM.
 * SHA-256 is computed via the Response streaming trick before uploading.
 */
export async function uploadWithProgress(
  file: File,
  serverUrls: string | string[],
  signer: Signer,
  onProgress: (e: UploadProgressEvent) => void,
): Promise<BlobDescriptor & { serverResults: ServerUploadResult[] }> {
  const servers = Array.isArray(serverUrls) ? serverUrls : [serverUrls];

  if (servers.length === 0) {
    throw new Error('No servers provided for upload');
  }

  const contentType = file.type || 'application/octet-stream';

  // Compute SHA-256 by streaming — avoids loading the entire video into RAM
  const fileHash = await sha256HexFromFile(file);

  // Build auth tokens for all servers
  const authTokens = await Promise.all(
    servers.map(serverUrl => buildAuthToken(signer, fileHash, serverUrl))
  );

  // Track progress across all uploads (report the average)
  const progressMap = new Map<string, UploadProgressEvent>();

  const aggregateProgress = () => {
    const values = Array.from(progressMap.values());
    if (values.length === 0) return;
    onProgress({
      loaded: Math.round(values.reduce((s, p) => s + p.loaded, 0) / values.length),
      total:  Math.round(values.reduce((s, p) => s + p.total,  0) / values.length),
      percent: Math.round(values.reduce((s, p) => s + p.percent, 0) / values.length),
    });
  };

  // Upload to all servers simultaneously; each XHR streams the File independently
  const serverResults: ServerUploadResult[] = await Promise.all(
    servers.map(async (serverUrl, index): Promise<ServerUploadResult> => {
      try {
        const result = await uploadFileToServer(
          file, contentType, serverUrl, fileHash, authTokens[index],
          (progress) => { progressMap.set(serverUrl, progress); aggregateProgress(); },
        );
        console.log(`✓ Uploaded video to ${serverUrl}: ${result.url}`);
        return { serverUrl, success: true, url: result.url };
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.warn(`✗ Upload to ${serverUrl} failed: ${msg}`);
        return { serverUrl, success: false, error: msg };
      }
    })
  );

  const successResult = serverResults.find(r => r.success && r.url);
  if (!successResult?.url) {
    const errors = serverResults.map(r => `${r.serverUrl}: ${r.error}`).join('; ');
    throw new Error(`All server uploads failed: ${errors}`);
  }

  return {
    url: successResult.url,
    sha256: fileHash,
    size: file.size,
    type: contentType,
    serverResults,
  };
}

/** Format bytes as a human-readable string (e.g. "12.4 MB / 250 MB"). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
